import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Pencil, ChevronDown, ChevronUp, GripVertical } from 'lucide-react';
import { Button, Badge, Modal } from '../ui';
import { surveyService } from '../../services/survey.service';
import { messageHandler } from '../../services/message-handler.service';
import { QuestionForm } from './QuestionForm';
import { OptionManager } from './OptionManager';
import type { SurveyQuestion, QuestionFormData } from '../../types/survey';

const typeLabels: Record<string, string> = {
  boolean: 'Sim/Não',
  text: 'Texto',
  single_choice: 'Escolha única',
  multiple_choice: 'Múltipla escolha',
};

const typeVariants: Record<string, 'default' | 'success' | 'warning' | 'danger'> = {
  boolean: 'success',
  text: 'default',
  single_choice: 'warning',
  multiple_choice: 'warning',
};

interface QuestionManagerProps {
  questionnaireId: number;
}

export const QuestionManager: React.FC<QuestionManagerProps> = ({ questionnaireId }) => {
  const queryClient = useQueryClient();
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editQuestion, setEditQuestion] = useState<SurveyQuestion | null>(null);

  const { data: questions = [], isLoading } = useQuery({
    queryKey: ['survey-questions', questionnaireId],
    queryFn: () => surveyService.listQuestions(questionnaireId),
  });

  const createMutation = useMutation({
    mutationFn: (data: QuestionFormData) => surveyService.createQuestion(questionnaireId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-questions', questionnaireId] });
      queryClient.invalidateQueries({ queryKey: ['surveys'] });
      setIsCreateOpen(false);
      messageHandler.success('Pergunta criada com sucesso');
    },
    onError: () => messageHandler.error('Erro ao criar pergunta'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: QuestionFormData }) =>
      surveyService.updateQuestion(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-questions', questionnaireId] });
      setEditQuestion(null);
      messageHandler.success('Pergunta atualizada com sucesso');
    },
    onError: () => messageHandler.error('Erro ao atualizar pergunta'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => surveyService.deleteQuestion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-questions', questionnaireId] });
      queryClient.invalidateQueries({ queryKey: ['surveys'] });
      messageHandler.success('Pergunta excluída com sucesso');
    },
    onError: () => messageHandler.error('Erro ao excluir pergunta'),
  });

  const handleDeleteQuestion = (q: SurveyQuestion) => {
    messageHandler.confirm(
      'Excluir Pergunta',
      `Tem certeza que deseja excluir a pergunta "${q.question_text}"?`,
      () => deleteMutation.mutate(q.id)
    );
  };

  const needsOptions = (type: string) => type === 'single_choice' || type === 'multiple_choice';

  if (isLoading) {
    return <div className="text-sm text-gray-500 py-4">Carregando perguntas...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {questions.length} pergunta{questions.length !== 1 ? 's' : ''}
        </span>
        <Button size="sm" onClick={() => setIsCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-1" />
          Nova Pergunta
        </Button>
      </div>

      {questions.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
          Nenhuma pergunta cadastrada. Clique em "Nova Pergunta" para começar.
        </div>
      ) : (
        <div className="space-y-2">
          {questions.map((q, idx) => (
            <div
              key={q.id}
              className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
            >
              <div className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800">
                <GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-sm text-gray-500 w-6 flex-shrink-0">{idx + 1}.</span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                    {q.question_text}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant={typeVariants[q.question_type]}>
                      {typeLabels[q.question_type]}
                    </Badge>
                    {q.is_required && (
                      <Badge variant="danger">Obrigatória</Badge>
                    )}
                    {needsOptions(q.question_type) && (
                      <span className="text-xs text-gray-500">
                        {q.options?.length || 0} opção(ões)
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {needsOptions(q.question_type) && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setExpandedId(expandedId === q.id ? null : q.id)}
                      title="Gerenciar opções"
                    >
                      {expandedId === q.id ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setEditQuestion(q)}
                    title="Editar"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => handleDeleteQuestion(q)}
                    title="Excluir"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {needsOptions(q.question_type) && expandedId === q.id && (
                <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4">
                  <OptionManager questionId={q.id} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} title="Nova Pergunta">
        <QuestionForm
          onSubmit={(data) => createMutation.mutate(data)}
          onCancel={() => setIsCreateOpen(false)}
          isLoading={createMutation.isPending}
        />
      </Modal>

      <Modal
        isOpen={!!editQuestion}
        onClose={() => setEditQuestion(null)}
        title="Editar Pergunta"
      >
        {editQuestion && (
          <QuestionForm
            question={editQuestion}
            onSubmit={(data) => updateMutation.mutate({ id: editQuestion.id, data })}
            onCancel={() => setEditQuestion(null)}
            isLoading={updateMutation.isPending}
          />
        )}
      </Modal>

    </div>
  );
};
